// App Store Connect service
import api from './authService'

export interface AppStoreCredentials {
  issuer_id: string
  key_id: string
  private_key: string
}

export interface AppStoreIntegration {
  connected: boolean
  issuer_id?: string
  key_id?: string
  last_synced_at?: string
  is_active?: boolean
  created_at?: string
}

export interface SyncJob {
  id: string
  job_type: string
  status: string
  started_at: string
  completed_at?: string
  records_synced: number
  error_message?: string
}

export interface AppStoreReview {
  review_id: string
  rating: number
  title: string | null
  review_body: string | null
  reviewer_nickname: string
  territory: string
  created_date: string
}

export interface AppStoreVersion {
  version_string: string
  release_type: string
  app_store_state: string
  release_date: string | null
  created_at: string
}

export interface AppStoreAppInfo {
  connected: boolean
  metadata: Record<string, any> | null
  reviews: AppStoreReview[]
  versions: AppStoreVersion[]
  averageRating: number | null
  reviewCount: number
}

export const appStoreService = {
  /**
   * Get App Store Connect integration status
   */
  async getCredentials(): Promise<AppStoreIntegration> {
    const response = await api.get('/api/appstore/credentials')
    return response.data
  },

  /**
   * Save App Store Connect credentials
   */
  async saveCredentials(credentials: AppStoreCredentials): Promise<void> {
    await api.post('/api/appstore/credentials', credentials)
  },

  /**
   * Delete/disable App Store Connect integration
   */
  async deleteCredentials(): Promise<void> {
    await api.delete('/api/appstore/credentials')
  },

  /**
   * Trigger manual sync
   */
  async triggerSync(): Promise<{ job_id: string }> {
    const response = await api.post('/api/appstore/sync')
    return response.data
  },

  /**
   * Get sync job status
   */
  async getSyncStatus(): Promise<{ jobs: SyncJob[] }> {
    const response = await api.get('/api/appstore/sync/status')
    return response.data
  },

  /**
   * Get App Store metadata, reviews, and versions for an app
   */
  async getAppMetadata(appId: string): Promise<AppStoreAppInfo> {
    const response = await api.get(`/api/appstore/metadata/${appId}`)
    return response.data
  },
}
